<template>
    <span
        :class="[
            'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium',
            statusClasses,
        ]"
    >
        <Icon v-if="statusIcon" :icon="statusIcon" :width="12" :height="12" />
        {{ statusLabel }}
    </span>
</template>

<script setup>
import { computed } from 'vue';
import Icon from '@/components/ui/icon/icon.vue';

const props = defineProps({
    status: {
        type: String,
        required: true,
    },
});

// Customer-facing presentation of the internal workflow statuses: internal
// bookkeeping stages (waiting_on_us, ready_for_invoicing) show as friendly
// phases, mirroring AbsBookingStatus::customerLabel().
const neutralClasses =
    'bg-slate-100 text-slate-700 border border-slate-200 dark:bg-slate-700 dark:text-slate-300 dark:border-slate-600';
const attentionClasses =
    'bg-orange-50 text-orange-700 border border-orange-200 dark:bg-orange-500/20 dark:text-orange-300 dark:border-orange-500/30';

const unsuccessfulConfig = {
    label: 'Unsuccessful',
    icon: 'close',
    classes:
        'bg-red-50 text-red-700 border border-red-200 dark:bg-red-500/20 dark:text-red-400 dark:border-red-500/30',
};

const statusConfig = {
    pending_payment: {
        label: 'Awaiting payment',
        icon: 'clock',
        classes:
            'bg-amber-50 text-amber-700 border border-amber-200 dark:bg-amber-500/20 dark:text-amber-400 dark:border-amber-500/30',
    },
    new: {
        label: 'Submitted',
        icon: 'edit',
        classes: neutralClasses,
    },
    sent_initial_email: {
        label: 'In progress',
        icon: 'clock',
        classes: neutralClasses,
    },
    waiting_on_customer: {
        label: 'Waiting on you',
        icon: 'clock',
        classes: attentionClasses,
    },
    waiting_on_us: {
        label: 'In progress',
        icon: 'clock',
        classes: neutralClasses,
    },
    ready_for_invoicing: {
        label: 'In progress',
        icon: 'clock',
        classes: neutralClasses,
    },
    invoice_sent: {
        label: 'Payment requested',
        icon: 'clock',
        classes: attentionClasses,
    },
    on_hold: {
        label: 'On hold',
        icon: 'clock',
        classes: neutralClasses,
    },
    success_sent_availability: {
        label: 'Availability found',
        icon: 'clock',
        classes: attentionClasses,
    },
    success_sent_booking_instructions: {
        label: 'Instructions sent',
        icon: 'clock',
        classes: attentionClasses,
    },
    success_confirmed_booked: {
        label: 'Booked',
        icon: 'check',
        classes:
            'bg-green-50 text-green-700 border border-green-200 dark:bg-green-500/20 dark:text-green-400 dark:border-green-500/30',
    },
    paid_and_done: {
        label: 'Completed',
        icon: 'check',
        classes:
            'bg-green-50 text-green-700 border border-green-200 dark:bg-green-500/20 dark:text-green-400 dark:border-green-500/30',
    },
    unsuccessful: unsuccessfulConfig,
    // The specific unsuccessful reasons are internal bookkeeping — every one
    // reads as plain "Unsuccessful" to the customer.
    unsuccessful_no_initial_response: unsuccessfulConfig,
    unsuccessful_ghosted: unsuccessfulConfig,
    unsuccessful_does_not_wish_to_proceed: unsuccessfulConfig,
    unsuccessful_no_available_options: unsuccessfulConfig,
    cancelled: {
        label: 'Cancelled',
        icon: 'close',
        classes:
            'bg-red-50 text-red-700 border border-red-200 dark:bg-red-500/20 dark:text-red-400 dark:border-red-500/30',
    },
};

const currentStatus = computed(() => {
    return statusConfig[props.status] || statusConfig.new;
});

const statusLabel = computed(() => currentStatus.value.label);
const statusIcon = computed(() => currentStatus.value.icon);
const statusClasses = computed(() => currentStatus.value.classes);
</script>
